import {
  CustomerServiceOutlined,
  DownloadOutlined,
  DownOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PhoneOutlined,
  ShareAltOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { Avatar, Button, Dropdown, Layout, message, theme } from 'antd';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SignInButton } from '../../SignInButton';
import './AppShell.css';

const { Header } = Layout;

interface NavItem {
  key: string;
  label: string;
  icon: JSX.Element;
  /** Route prefixes that count as "on this section" (defaults to [key]). */
  prefixes?: string[];
}

/** Global destinations — the same set the old hamburger exposed, now always visible. */
const NAV_ITEMS: NavItem[] = [
  {
    key: '/client',
    label: 'Clients',
    icon: <TeamOutlined />,
    // Client-scoped sections live under several path roots; all map to Clients.
    prefixes: [
      '/client',
      '/agentSms',
      '/onboardingNew',
      '/lead-engagements',
      '/agentLeads',
    ],
  },
  {
    key: '/admin/support-tickets',
    label: 'Support',
    icon: <CustomerServiceOutlined />,
  },
  {
    key: '/admin/broadcast-messages',
    label: 'Broadcast',
    icon: <ShareAltOutlined />,
  },
  {
    key: '/admin/lead-engagement-test',
    label: 'LE Test Call',
    icon: <PhoneOutlined />,
  },
  {
    key: '/resources',
    label: 'Resources',
    icon: <DownloadOutlined />,
  },
  {
    key: '/admin/conference-report',
    label: 'Lead Engagement Audit',
    icon: <FileTextOutlined />,
  },
];

/** Highlight the nav item whose longest matching route prefix covers the path. */
export function activeKey(pathname: string): string | undefined {
  let best: string | undefined;
  let bestLen = -1;
  for (const item of NAV_ITEMS) {
    for (const p of item.prefixes ?? [item.key]) {
      if (
        (pathname === p || pathname.startsWith(p + '/')) &&
        p.length > bestLen
      ) {
        bestLen = p.length;
        best = item.key;
      }
    }
  }
  return best;
}

/**
 * The single, always-present application chrome: brand, primary navigation, and
 * the user menu. Replaces the collapsed corner hamburger (NavMenu) so there is
 * one stable anchor on every authenticated page.
 */
export function AppShell(): JSX.Element {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const { token } = theme.useToken();
  const account = accounts[0];

  const active = activeKey(location.pathname);
  const accountName = account?.name ?? account?.username ?? 'Account';
  const [userOpen, setUserOpen] = useState(false);
  const signOut = () => {
    void instance
      .logoutRedirect({ postLogoutRedirectUri: '/' })
      .catch(() => void message.error('Sign-out failed. Please try again.'));
  };

  return (
    <Header
      className="app-shell-header"
      // Seed the brand accent once so the CSS file can use var(--hw-accent).
      style={{ ['--hw-accent' as string]: token.colorPrimary }}
    >
      <Link className="app-shell-brand" to="/">
        <span aria-hidden="true" style={{ fontSize: 18 }}>
          ⬡
        </span>{' '}
        HouseWhisper
      </Link>

      <AuthenticatedTemplate>
        <nav className="app-nav">
          {NAV_ITEMS.map(({ key, label, icon }) => {
            const isActive = key === active;
            return (
              <Link
                aria-current={isActive ? 'page' : undefined}
                className={`app-nav-link${isActive ? ' is-active' : ''}`}
                key={key}
                to={key}
              >
                {icon} {label}
              </Link>
            );
          })}
        </nav>
        <Dropdown
          dropdownRender={() => (
            <div className="app-shell-user-menu">
              <div className="app-shell-user-menu__name">{accountName}</div>
              <Button
                autoFocus
                block
                icon={<LogoutOutlined />}
                onClick={signOut}
                size="large"
                type="primary"
              >
                Sign Out
              </Button>
            </div>
          )}
          onOpenChange={setUserOpen}
          open={userOpen}
          trigger={['click']}
        >
          <button
            aria-expanded={userOpen}
            aria-haspopup="dialog"
            className="app-shell-user"
            type="button"
          >
            <Avatar
              icon={<UserOutlined />}
              size={28}
              style={{ background: token.colorPrimary, flex: 'none' }}
            />
            <span className="app-shell-user__name">{accountName}</span>
            <DownOutlined
              style={{ fontSize: 10, color: '#8c8c8c', flex: 'none' }}
            />
          </button>
        </Dropdown>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <span style={{ flex: 1 }} />
        {/* Reuse SignInButton so header + hero share one login path
            (origin-matched redirectUri + rejection handling). */}
        <SignInButton loginType="popup" />
      </UnauthenticatedTemplate>
    </Header>
  );
}
